import { Injectable } from "@angular/core";
import { PRIMITIVE, ServiceMessageBrokerFactory } from "shared/provider-webworker";

import { CurrencyPairCalendarLookupService } from "./CurrencyPairCalendarLookupService";
import { PrimaryLookupService } from "../../impl/PrimaryLookupService";
import { ICurrencyPairCalendarPromiseLookupService } from "../ICurrencyPairCalendarPromiseLookupService";

import * as CubiqTS from "shared/cubiq-model-multi-asset";
import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

import { CcyPairPromiseLookupService } from "./CcyPairPromiseLookupService";

import { MappedCurrencyPairCalendarImpl } from "../../../model/mapped/MappedCurrencyPairCalendarImpl";
import { Subject, Observable } from "rxjs";
import { NotificationService } from "../../notification/impl/NotificationService";
import { CubiqWorker } from "../../../worker/CubiqWorker";

export const EVENT_TYPE_SYSTEM_TRADE_DATE_ROLLOVER_EVENT: string = "SysTradeDateRolloverEvent";

@Injectable()
export class CurrencyPairCalendarPrimaryLookupService
  extends PrimaryLookupService<string, CubiqTS.CurrencyPairCalendarImpl>
  implements ICurrencyPairCalendarPromiseLookupService
{
  private _tdrSubject = new Subject<Set<string>>();
  private holidaysPrimed: Set<string> = new Set();

  constructor(
    protected base: CurrencyPairCalendarLookupService,
    protected factory: ServiceMessageBrokerFactory,
    protected cubiqWorker: CubiqWorker,
    protected ccyPairService: CcyPairPromiseLookupService,
    protected notificationService: NotificationService,
  ) {
    super(
      base,
      factory,
      CubiqTS.CurrencyPairCalendarImpl,
      cubiqWorker,
      CubiqTS.CurrencyPairCalendarImpl.name,
    );

    cubiqWorker.isAuth(`${this.constructor.name}`).then(
      () => {
        this._primed.resolve(true);
      },
      (reason) => {
        console.debug("CurrencyPairCalendarPrimaryLookupService: Failed to prime: " + reason);

        this._primed.resolve(false);
      },
    );

    this.broker.registerMethod(
      "getCalendarWithHolidays",
      [PRIMITIVE, PRIMITIVE, PRIMITIVE],
      this.getCalendarWithHolidays.bind(this),
      this.VALUE,
    );
    this.broker.registerMethod(
      "getFixingDate",
      [PRIMITIVE, PRIMITIVE, PRIMITIVE],
      this.getFixingDate.bind(this),
      PRIMITIVE,
    );

    this.cubiqWorker
      .getEncodableObservable(CubiqTS.TradingCalendarSnapShotImpl.name)
      .subscribe(this.onTradingCalendarSnapShot.bind(this));

    this.cubiqWorker
      .getEncodableObservable(CubiqTS.ApplicationMessageEventImpl.name)
      .subscribe(this.onSystemTradeDateRollover.bind(this));

    this.cubiqWorker
      .getBroker()
      .primeStaticData<CubiqTS.StaticDataProfileImpl>(CubiqTS.StaticDataProfileImpl.name)
      .subscribe((profile: CubiqTS.StaticDataProfileImpl) => {
        if (profile != null && profile.systemTradeDateRoll != null && this.base != null) {
          logger.out.info(
            `${
              this.constructor.name
            } Setting system Trade Date Rollover to ${profile.systemTradeDateRoll.format()}`,
          );
          this.base.systemTradeDateRollover = profile.systemTradeDateRoll;
        }
      });
  }

  getTdrNotification(): Observable<Set<string>> {
    return this._tdrSubject.asObservable();
  }

  protected onTradingCalendarSnapShot(data: CubiqTS.TradingCalendarSnapShotImpl): void {
    if (data instanceof CubiqTS.TradingCalendarSnapShotImpl) {
      if (Array.isArray(data.calendarRun)) {
        let pairs = data.calendarRun.filter(this.shouldProcessRun.bind(this)).map((run) => {
          return this.processTradingCalendarRun(run);
        });

        this._tdrSubject.next(new Set<string>(pairs));
      }
    }
  }

  protected processTradingCalendarRun(calendarRun: CubiqTS.TradingCalendarRun): string {
    // delete primed holidays map, the calendar update could be a result of creating/updateing/deleting a holiday
    // entry screen will need to ask the server again for them
    if (this.holidaysPrimed.has(calendarRun.baseCcyCd + calendarRun.nonBaseCcyCd)) {
      this.holidaysPrimed.delete(calendarRun.baseCcyCd + calendarRun.nonBaseCcyCd);
    }
    let ccyPairCalendar: MappedCurrencyPairCalendarImpl = this.base.getItem(
      `${calendarRun.baseCcyCd}${calendarRun.nonBaseCcyCd}`,
    ) as MappedCurrencyPairCalendarImpl;
    let create = false;

    if (!ccyPairCalendar) {
      ccyPairCalendar = new MappedCurrencyPairCalendarImpl();
      ccyPairCalendar.baseCcy = calendarRun.baseCcyCd;
      ccyPairCalendar.nonBaseccy = calendarRun.nonBaseCcyCd;
      create = true;
    }

    // we always want these
    ccyPairCalendar.tradeDateRollDate = calendarRun.tradeDateRollDate;
    ccyPairCalendar.tradeDateRollTime = calendarRun.tradeDateRollTime;

    if (CubiqTS.Product.NDF === calendarRun.product) {
      let tenorToValueDateMap = new Map();
      let tenorToValueAndFixingDateMap = new Map();

      ccyPairCalendar.isNdfEnabled = true;
      ccyPairCalendar.tenorToValueAndFixingDateSet = calendarRun.ndfValueDates;
      ccyPairCalendar.tenorToValueDateSet = calendarRun.valueDates;

      for (let tenorToValue of ccyPairCalendar.tenorToValueDateSet) {
        tenorToValueDateMap.set(tenorToValue.tenor, tenorToValue);
      }
      ccyPairCalendar.tenorToValueDateMap = tenorToValueDateMap;

      for (let tenorToValueAndFixingDate of ccyPairCalendar.tenorToValueAndFixingDateSet) {
        tenorToValueAndFixingDateMap.set(
          tenorToValueAndFixingDate.tenor,
          tenorToValueAndFixingDate,
        );
      }
      ccyPairCalendar.tenorToValueAndFixingDateMap = tenorToValueAndFixingDateMap;
    }

    if (CubiqTS.Product.FWD === calendarRun.product) {
      ccyPairCalendar.tenorToValueDateSet = calendarRun.valueDates;
      let tenorToValueDateMap = new Map();
      for (let tenorToValue of ccyPairCalendar.tenorToValueDateSet) {
        tenorToValueDateMap.set(tenorToValue.tenor, tenorToValue);
      }
      ccyPairCalendar.tenorToValueDateMap = tenorToValueDateMap;
    }

    if (create) this.base.createItem(ccyPairCalendar);
    else this.base.updateItem(ccyPairCalendar);

    return `${calendarRun.baseCcyCd}${calendarRun.nonBaseCcyCd}`;
  }

  protected shouldProcessRun(calendarRun: CubiqTS.TradingCalendarRunImpl): boolean {
    let shouldProcess: boolean = calendarRun != null;

    if (calendarRun != null && calendarRun.errors != null) {
      logger.out.error(
        `${this.constructor.name}: Trading calendar reported error for pair ${calendarRun.baseCcyCd}${calendarRun.nonBaseCcyCd}: ${calendarRun.errors}`,
      );
      let msg: string = this.notificationService.translationService.instant(
        "error.loading.currency.pair",
        [`${calendarRun.baseCcyCd}${calendarRun.nonBaseCcyCd}`],
      );
      let title: string =
        this.notificationService.translationService.instant("error.loading.title");
      this.notificationService.handleGenericError(calendarRun.errors, msg, title);
      shouldProcess = false;
    }

    return shouldProcess;
  }

  public requestRun(ccyPairs: Array<string> | null): Promise<CubiqTS.CurrencyPairCalendar> {
    let request = new CubiqTS.TradingCalendarRequestImpl();

    return new Promise<CubiqTS.CurrencyPairCalendar>((res, rej) => {
      this.cubiqWorker.isAuth().then(
        () => {
          return this.cubiqWorker.postServiceRequest(request).then(
            (data) => {
              if (data instanceof CubiqTS.TradingCalendarSnapShotImpl) {
                if (Array.isArray(data.calendarRun)) {
                  let pairs = data.calendarRun
                    .filter(this.shouldProcessRun.bind(this))
                    .map((run) => {
                      return this.processTradingCalendarRun(run);
                    });
                  this._tdrSubject.next(new Set<string>(pairs));
                  res(null);
                } else {
                  let reason = new Error(`${this.constructor.name}.requestRun() invalid response`);
                  logger.out.error(reason);
                  rej(reason);
                }
              } else {
                let reason = new Error(`${this.constructor.name}.requestRun() invalid response`);
                logger.out.error(reason);
                rej(reason);
              }
            },
            (err) => rej(err),
          );
        },
        (reason) => {
          logger.out.error(`${this.constructor.name}.requestRun()`);
          logger.out.error(reason);
        },
      );
    });
  }

  public getCalendarWithHolidays(
    base: string,
    nonBase: string,
    product: CubiqTS.Product,
    strictInvert: boolean = false,
  ): Promise<CubiqTS.CurrencyPairCalendar> {
    let request = new CubiqTS.HolidayCalendarRequestImpl();
    request.baseCcyCd = base;
    request.nonBaseCcyCd = nonBase;
    request.product = product;

    return new Promise<CubiqTS.CurrencyPairCalendar>((res, rej) => {
      this.cubiqWorker.isAuth().then(
        () => {
          if (this.holidaysPrimed.has(base + nonBase + product)) {
            res(this.getItem(base + nonBase));
          } else if (this.holidaysPrimed.has(nonBase + base + product)) {
            res(this.getItem(nonBase + base));
          } else {
            return this.cubiqWorker.postServiceRequest(request).then(
              (data) => {
                if (data instanceof CubiqTS.CurrencyPairCalendarImpl) {
                  let ccyPairCalendar: MappedCurrencyPairCalendarImpl = this.base.getItem(
                    `${base}${nonBase}`,
                  ) as MappedCurrencyPairCalendarImpl;
                  if (ccyPairCalendar) {
                    if (CubiqTS.Product.NDF == product) {
                      ccyPairCalendar.ndfHolidays = data.holidays;
                    } else {
                      ccyPairCalendar.holidays = data.holidays;
                    }
                    ccyPairCalendar.tradeDateRollDate = data.tradeDateRollDate;
                    ccyPairCalendar.tradeDateRollTime = data.tradeDateRollTime;

                    if (data.tenorToValueAndFixingDateSet) {
                      data.tenorToValueAndFixingDateSet.sort((a, b) => a.tenor - b.tenor);
                    }
                    if (data.tenorToValueDateSet) {
                      data.tenorToValueDateSet.sort((a, b) => a.tenor - b.tenor);
                    }
                    ccyPairCalendar.tenorToValueDateSet = [...data.tenorToValueDateSet];
                    ccyPairCalendar.tenorToValueAndFixingDateSet = [
                      ...data.tenorToValueAndFixingDateSet,
                    ];

                    let tenorToValueDateMap = new Map();
                    for (let tenorToValue of ccyPairCalendar.tenorToValueDateSet) {
                      tenorToValueDateMap.set(tenorToValue.tenor, tenorToValue);
                    }
                    ccyPairCalendar.tenorToValueDateMap = tenorToValueDateMap;

                    let tenorToValueAndFixingDateMap = new Map();
                    for (let tenorToValueAndFixingDate of ccyPairCalendar.tenorToValueAndFixingDateSet) {
                      tenorToValueAndFixingDateMap.set(
                        tenorToValueAndFixingDate.tenor,
                        tenorToValueAndFixingDate,
                      );
                    }
                    ccyPairCalendar.tenorToValueAndFixingDateMap = tenorToValueAndFixingDateMap;

                    // NBTOD - this is duplicated elsewhere - refactor
                    if (CubiqTS.Product.NDF === product) {
                      ccyPairCalendar.isNdfEnabled = true;
                    }

                    this.base.updateItem(ccyPairCalendar);

                    let key: string =
                      ccyPairCalendar.baseCcy + ccyPairCalendar.nonBaseccy + product;
                    if (!this.holidaysPrimed.has(key)) {
                      this.holidaysPrimed.add(key);
                    }
                  }
                  if (CubiqTS.Product.NDF == product) {
                    let cal = Object.assign(
                      new CubiqTS.CurrencyPairCalendarImpl(),
                      ccyPairCalendar,
                    );
                    cal.holidays = ccyPairCalendar.ndfHolidays;
                    res(cal);
                  } else {
                    res(ccyPairCalendar);
                  }
                } else {
                  let reason = new Error(
                    `${this.constructor.name}.getCalendarWithHolidays() invalid response`,
                  );
                  logger.out.error(reason);
                  rej(reason);
                }
              },
              (err) => rej(err),
            );
          }
        },
        (reason) => {
          logger.out.error(`${this.constructor.name}.getCalendarWithHolidays()`);
          logger.out.error(reason);
        },
      );
    });
  }

  public getFixingDate(base: string, nonBase: string, brokenDate: Date): Promise<Date> {
    let request = new CubiqTS.NdfFixingDateLookupRequestImpl();
    request.baseCcyCode = base;
    request.nonBaseCcyCode = nonBase;
    request.valueDate = new CubiqTS.StandardDateImpl();
    request.valueDate.day = brokenDate.getDate();
    request.valueDate.month = brokenDate.getMonth() + 1;
    request.valueDate.year = brokenDate.getFullYear();

    return new Promise<Date>((res, rej) => {
      this.cubiqWorker.isAuth().then(() => {
        return this.cubiqWorker.postServiceRequest(request).then((data) => {
          if (data instanceof CubiqTS.StandardDateImpl) {
            let date: Date = new Date(data.year, data.month - 1, data.day);
            res(date);
          } else {
            res(null);
          }
        });
      });
    });
  }

  /**
   * When a system trade date rollover event is received in an application message
   * update the system trade date rollover property in the base lookup
   * @param msg the application message holding the system trade date rollover event
   */
  public onSystemTradeDateRollover(msg: CubiqTS.ApplicationMessageEventImpl): void {
    try {
      if (
        msg != null &&
        msg.messageType == EVENT_TYPE_SYSTEM_TRADE_DATE_ROLLOVER_EVENT &&
        msg.message instanceof CubiqTS.SysTradeDateRolloverEventImpl
      ) {
        logger.out.info(`${this.constructor.name}: Processing system trade date rollover event`);

        let systemTradeDateRollEvent: CubiqTS.SysTradeDateRolloverEventImpl = msg.message;

        if (systemTradeDateRollEvent.nextSystemTradeDateRoll != null) {
          this.base.systemTradeDateRollover = systemTradeDateRollEvent.nextSystemTradeDateRoll;
          this.base.previousTradeDataRollover = systemTradeDateRollEvent.rolledSystemTradeDate;
          logger.out.info(
            `${
              this.constructor.name
            } Updated system Trade Date Rollover to ${this.base.systemTradeDateRollover.format()}`,
          );
        } else {
          logger.out.info(
            `${this.constructor.name} Event has no next Trade Date Rollover. Skipping update.`,
          );
        }
      }
    } catch (e) {
      logger.out.error(
        `${this.constructor.name}: Failed to process system trade date rollover event ${e}`,
      );
    }
  }
}
